import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Reply, Loader2, MessagesSquare } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Skool-style feed of a user's community activity on their profile page.
 * - Topics they started + replies they posted, merged chronologically.
 * - Paginated via `?before=<iso>` cursor for infinite-scroll style Load More.
 * - Only rendered when the viewer is signed in — the parent handles that gate.
 */
function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function CategoryChip({ category }) {
  if (!category) return null;
  const color = category.color || "#0D9488";
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 border font-mono-num text-[9px] uppercase tracking-widest"
      style={{ borderColor: `${color}66`, color, background: `${color}0d` }}
      data-testid={`activity-category-${category.slug || category.name}`}
    >
      {category.name}
    </span>
  );
}

function ActivityItem({ item, displayName }) {
  const isTopic = item.type === "topic";
  const Icon = isTopic ? MessageSquare : Reply;
  const href = isTopic ? `/community/${item.topic_id}` : `/community/${item.topic_id}#reply-${item.id}`;
  const heading = isTopic ? item.title : `Replied to "${item.topic_title}"`;
  return (
    <Link
      to={href}
      className="block border border-slate-200 bg-white hover:border-[#0D9488]/50 hover:bg-slate-50/40 transition-colors p-4 group"
      data-testid={`activity-item-${item.type}-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-8 h-8 border ${isTopic ? "border-[#0D9488]/40 text-[#0D9488] bg-[#0D9488]/5" : "border-slate-200 text-slate-500 bg-slate-50"} flex items-center justify-center`}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {isTopic ? "// Started a topic" : "// Replied"}
            </span>
            <CategoryChip category={item.category} />
            <span className="font-mono-num text-[10px] text-slate-400 uppercase tracking-widest ml-auto">
              {timeAgo(item.at)}
            </span>
          </div>
          <div className="font-display font-black uppercase tracking-tight text-slate-900 text-base mt-1 truncate group-hover:text-[#0D9488] transition-colors">
            {heading}
          </div>
          {item.body_snippet && (
            <div className="text-slate-600 text-sm mt-1 line-clamp-2 leading-relaxed">
              {item.body_snippet}
            </div>
          )}
          {isTopic && (
            <div className="mt-2 font-mono-num text-[10px] text-slate-500 uppercase tracking-widest">
              {item.reply_count} {item.reply_count === 1 ? "reply" : "replies"}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CommunityActivityFeed({ userId, displayName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadPage = useCallback(async (before) => {
    if (!userId) return;
    const isFirst = !before;
    if (isFirst) setLoading(true);
    else setLoadingMore(true);
    try {
      const { data } = await api.get(`/users/${userId}/community_activity`, {
        params: { limit: 20, ...(before ? { before } : {}) },
      });
      setItems((cur) => (isFirst ? data.items : [...cur, ...data.items]));
      setHasMore(!!data.has_more);
      setNextBefore(data.next_before || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      if (isFirst) setLoading(false);
      else setLoadingMore(false);
    }
  }, [userId]);

  useEffect(() => { loadPage(null); }, [loadPage]);

  return (
    <section className="mt-8" data-testid="community-activity-feed">
      <div className="flex items-center gap-2 mb-3 px-2">
        <MessagesSquare className="w-4 h-4 text-[#0D9488]" />
        <div className="font-mono-num text-[10px] text-[#0D9488] uppercase tracking-[0.3em]">// Community Activity</div>
      </div>
      <h2 className="font-display font-black uppercase tracking-tight text-xl sm:text-2xl text-slate-900 mb-4 px-2">
        {displayName ? `${displayName}'s posts & replies` : "Posts & replies"}
      </h2>

      {loading ? (
        <div className="border border-slate-200 bg-white p-6 text-slate-500 text-sm animate-pulse" data-testid="activity-loading">
          Loading activity…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-slate-200 bg-white p-8 text-center" data-testid="activity-empty">
          <MessagesSquare className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-1">// No community activity yet</div>
          <div className="text-slate-600 text-sm">
            Nothing to show — this user hasn&apos;t started or replied to any discussions yet.
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="activity-list">
            {items.map((it) => (
              <ActivityItem key={`${it.type}-${it.id}`} item={it} displayName={displayName} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => loadPage(nextBefore)}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-600 px-4 h-9 text-xs uppercase tracking-widest font-mono-num transition-colors disabled:opacity-50"
                data-testid="activity-load-more"
              >
                {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
